import React from 'react';

import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { SubscriberCouncilPartner } from '@interfaces/partners';
import { ClickTarget, ContentType, TrackingLocation, click } from '@tracking';
import { isExternalURL } from '@utils/url_validations';

import Button from '../Button';
import IconArrowDown from '../Images/Rebranding/IconArrowDown';

const SubscriberCouncil = ({
  subscriberCouncil,
}: {
  subscriberCouncil: SubscriberCouncilPartner[];
}): JSX.Element => {
  const hydrated = useServerSideRendering();
  const { Track, trackEvent } = useEventTracking({
    contentType: ContentType.SubscriberCouncil,
    location: TrackingLocation.SubscriberCouncil,
  });

  return (
    <Track>
      <aside className="pt-28 sm:pt-0" data-testid="subscriber-council">
        <header className="mb-32 flex flex-col items-baseline justify-between border-0 border-b border-solid border-grayscale-gray pb-12 sm:flex-row">
          <h4 className="m-0 mb-4 font-suisse-intl-mono text-caption-lg font-normal text-black sm:mb-0">
            The Information Subscriber Council
          </h4>
          <a
            href="/partners#partnerships"
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.LearnMore }))}
            className="font-suisse-intl-mono text-caption-sm text-primary-magenta transition-colors duration-200 hover:text-black"
          >
            Learn more{' '}
            <IconArrowDown className="inline w-12 -rotate-90 fill-current stroke-current" />
          </a>
        </header>
        <ul className="m-0 grid list-none gap-x-80 gap-y-24 p-0 sm:grid-cols-2 sm:gap-y-48 md:grid-cols-3">
          {subscriberCouncil.length > 0 &&
            subscriberCouncil.map((partner) => (
              <li key={partner.id}>{hydrated && <SubscriberCouncil.Link {...partner} />}</li>
            ))}
        </ul>
      </aside>
    </Track>
  );
};

export default SubscriberCouncil;

SubscriberCouncil.Link = function SubscriberCouncilLink({
  id,
  name,
  tagline,
  targetLink,
}: SubscriberCouncilPartner): JSX.Element {
  const { trackEvent } = useEventTracking();
  const externalLinkAttributes = isExternalURL(targetLink, window.location)
    ? { rel: 'sponsored' }
    : {};

  return (
    <Button
      as="externalLink"
      className="font-suisse-screen text-headline-alt-sm text-black transition-colors duration-200 hover:text-primary-magenta"
      href={targetLink}
      onClickCapture={() =>
        trackEvent(
          click({
            contentId: id,
            clickTarget: ClickTarget.SubscriberCouncilName,
          })
        )
      }
      {...externalLinkAttributes}
    >
      {name}
      <span className="hover:text-current! mt-6 block text-disclosure-lg leading-snug text-black">
        {tagline}
      </span>
    </Button>
  );
};
